import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Renderer2,
  ViewChild,
  HostBinding,
  RendererStyleFlags2,
  NgZone,
} from '@angular/core';
import { DsTooltipOptions, DsTooltipPosition, DsVisibilityState } from '../../tooltip.interfaces';
import { dsUpdateComponentCssVars } from '@skyeng/libs/shared/ui-kit-v2/theme';
import { DS_TOOLTIP_CSS_VARS_PREFIX, DS_TOOLTIP_CUSTOMIZABLE_PARAMS } from '../../tooltip.consts';
import { Store } from '@skyeng/libs/shared/store';
import { combineLatest, fromEvent, NEVER, Observable, timer } from 'rxjs';
import { DecorateUntilDestroy, returnToAngularZone, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { tooltipVisibilityAnimation } from './tooltip-animation';
import { shareReplay, switchMap } from 'rxjs/operators';
import { DS_CSS_VAR_PREFIX } from '@skyeng/libs/shared/ui-kit-v2/base';

interface TooltipStoreEvents {
  componentHidden: void;
}

const XS_BUTTON_HEIGHT_PX = 36;

const TOOLTIP_INITIAL_STATE = {
  visibilityValue: 'initial' as DsVisibilityState,
  preventHiding: false,
  options: {} as DsTooltipOptions,
};

const DS_TOOLTIP_ARROW_OFFSET_CSS_VAR = `${DS_CSS_VAR_PREFIX}-${DS_TOOLTIP_CSS_VARS_PREFIX}-arrow-offset`;

@DecorateUntilDestroy()
@Component({
  selector: 'ds-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [tooltipVisibilityAnimation],
})
export class DsTooltipComponent implements AfterViewInit {
  @ViewChild('root', { static: true }) rootRef: ElementRef<HTMLElement> | null;
  @ViewChild('body') bodyRef: ElementRef<HTMLElement>;

  @HostBinding('class.-ignore-pointer-events') ignorePointerEvents: boolean | undefined = false;

  private store = new Store<typeof TOOLTIP_INITIAL_STATE, TooltipStoreEvents>(TOOLTIP_INITIAL_STATE);
  private type: string;
  private options$ = this.store.select('options');

  options: DsTooltipOptions = {};
  hidden$: Observable<void> = this.store.on('componentHidden');
  visibilityValue$ = this.store.select('visibilityValue').pipe(shareReplay({ refCount: true, bufferSize: 1 }));
  preventHiding$ = this.store.select('preventHiding');
  bodyDirection: 'vertical' | 'horizontal';
  position: DsTooltipPosition;
  arrow = require('!html-loader!image-webpack-loader?{}!./images/arrow.svg').default;

  constructor(
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef,
    private ngZone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    // wait one loop to render child content under *ngIf
    timer(0)
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => {
        const height = this.bodyRef.nativeElement.offsetHeight;
        this.bodyDirection = height > XS_BUTTON_HEIGHT_PX ? 'vertical' : 'horizontal';
        this.changeDetectorRef.detectChanges();
      });
    this.enableClosingTooltipWithClickFromOutsideWhenNeeded();
  }

  show(): void {
    this.setVisibility('visible');
  }

  hide(): void {
    this.setVisibility('hidden');
  }

  onMouseEnter(): void {
    if (this.options.disabled || !this.options.interactive) {
      return;
    }

    this.setPreventHiding(true);
  }

  onMouseLeave(): void {
    if (this.options.disabled) {
      return;
    }

    this.setPreventHiding(false);
  }

  onVisibilityAnimationDone(): void {
    if (this.store.get('visibilityValue') === 'hidden') {
      this.store.fire('componentHidden');
    }
  }

  setPreventHiding(preventHiding: boolean): void {
    this.store.updateFields({ preventHiding });
  }

  setOptions(options: DsTooltipOptions): void {
    this.options = options;
    this.store.updateField('options', options);

    if (this.options.type && this.options.type !== this.type) {
      this.ignorePointerEvents = this.options.ignorePointerEvents;
      this.type = this.options.type;
      dsUpdateComponentCssVars({
        renderer: this.renderer,
        rootElement: this.rootRef,
        componentPrefix: DS_TOOLTIP_CSS_VARS_PREFIX,
        params: DS_TOOLTIP_CUSTOMIZABLE_PARAMS,
        type: this.type,
      });
    }

    this.changeDetectorRef.markForCheck();
  }

  setPosition(position: DsTooltipPosition): void {
    if (position === this.position) {
      return;
    }

    this.position = position;

    this.changeDetectorRef.markForCheck();
  }

  setArrowOffset(arrowOffset: number): void {
    if (!this.rootRef) {
      return;
    }

    this.renderer.setStyle(
      this.rootRef.nativeElement,
      DS_TOOLTIP_ARROW_OFFSET_CSS_VAR,
      `${arrowOffset}px`,
      RendererStyleFlags2.DashCase,
    );

    this.changeDetectorRef.markForCheck();
  }

  private setVisibility(value: DsVisibilityState): void {
    if (!(value === 'hidden' && this.store.get('preventHiding'))) {
      this.store.updateField('visibilityValue', value);
    }
  }

  private enableClosingTooltipWithClickFromOutsideWhenNeeded(): void {
    this.ngZone.runOutsideAngular(() => {
      combineLatest(this.visibilityValue$, this.options$)
        .pipe(
          switchMap(([visibility, options]) => {
            if (visibility !== 'visible' || !options.disabled || !options.closeOnClickOutside) {
              return NEVER;
            }

            return fromEvent<MouseEvent>(document, options.isMobile ? 'touchStart' : 'click', { capture: true });
          }),
          returnToAngularZone(this.ngZone),
          takeUntilDestroyed(this),
        )
        .subscribe(mouseClick => {
          const target = mouseClick.target as Node;

          if (document.contains(target) && !this.elementRef.nativeElement.contains(target)) {
            this.hide();
          }
        });
    });
  }
}
